import React, { useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { AnnouncementWithStatus } from "../../../graphql/generated/graphql";
import { colors, spacing, radii } from "../../../styles/design-system";

interface StudentAnnouncementCardProps {
  announcement: AnnouncementWithStatus;
  isInGrid?: boolean;
  onPress?: () => void;
}

const StudentAnnouncementCard: React.FC<StudentAnnouncementCardProps> = ({
  announcement,
  isInGrid = false,
  onPress,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isNew = announcement.isNew ?? false;

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();
  const stripHtmlTags = (html: string) => html.replace(/<[^>]*>/g, "");

  const handleReadMoreToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const messageText = stripHtmlTags(announcement.message);
  const shouldShowReadMore = messageText.length > 150;

  return (
    <View style={[styles.announcementCard, isInGrid && !isExpanded && styles.gridCard]}>
      <View style={styles.cardHeader}>
        <View style={styles.cardHeaderLeft}>
          {isNew && (
            <View style={styles.newBadge}>
              <CustomText
                text="New"
                size="xs"
                weight="ultraBold"
                style={styles.newBadgeText}
                useTranslationText={false}
              />
            </View>
          )}
          <Pressable onPress={onPress}>
            {({ hovered }: any) => (
              <CustomText
                text={announcement.title}
                size="s"
                weight="ultraBold"
                style={[styles.announcementTitle, isNew && styles.titleWithBadge, hovered && styles.titleHovered]}
                useTranslationText={false}
              />
            )}
          </Pressable>
          <CustomText text={announcement.author.name} size="xs" style={styles.authorText} useTranslationText={false} />
        </View>
        <CustomText
          text={formatDate(announcement.created)}
          size="s"
          weight="medium"
          style={styles.dateText}
          useTranslationText={false}
        />
      </View>

      <CustomSpacing type="vertical" size="m" />

      <View style={[styles.messageContainer, isInGrid && !isExpanded && styles.messageContainerGrid]}>
        <CustomText
          text={isExpanded || !shouldShowReadMore ? messageText : `${messageText.substring(0, 150)}...`}
          size="s"
          style={styles.messageText}
          useTranslationText={false}
        />
      </View>

      {shouldShowReadMore && (
        <>
          <CustomSpacing type="vertical" size="m" />
          <Pressable onPress={handleReadMoreToggle} style={styles.readMoreButton}>
            <CustomText
              text={isExpanded ? "Read Less" : "Read More"}
              size="s"
              style={styles.readMoreText}
              useTranslationText={false}
            />
          </Pressable>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  announcementCard: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: spacing.md,
    width: "100%",
  },
  gridCard: {
    height: 150,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  cardHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    marginRight: 12,
  },
  newBadge: {
    backgroundColor: colors.accent.ocean500,
    borderRadius: radii.full,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xxs,
    marginRight: 10,
    flexShrink: 0,
  },
  newBadgeText: {
    color: colors.white,
    fontSize: 10,
  },
  announcementTitle: {
    color: colors.grey[700],
    flexShrink: 1,
  },
  titleWithBadge: {
    flexShrink: 1,
  },
  titleHovered: {
    textDecorationLine: "underline",
  },
  authorText: {
    color: colors.grey[500],
    marginLeft: spacing.xs,
    flexShrink: 0,
  },
  dateText: {
    color: colors.grey[500],
    flexShrink: 0,
  },
  messageContainer: {
    flex: 1,
  },
  messageContainerGrid: {
    maxHeight: 40,
    overflow: "hidden",
  },
  messageText: {
    color: colors.grey[500],
    lineHeight: 20,
  },
  readMoreButton: {
    alignSelf: "flex-start",
  },
  readMoreText: {
    color: colors.brand[500],
    fontWeight: "600",
  },
});

export default StudentAnnouncementCard;
